import { fetchAutocompleteResults } from '@/app/api/search'
import {
	DEFAULT_ALLOCATION,
	DEFAULT_CHECK_IN_DATE,
	DEFAULT_CHECK_OUT_DATE,
	DEFAULT_CURRENCY,
	DEFAULT_HOTEL_CHECK_IN_DATE,
	DEFAULT_HOTEL_CHECK_OUT_DATE,
	FORM_TYPE,
	PROPERTY_TYPE,
	RATING_COLOR,
	RATING_THRESHOLD,
	STAY_OPTIONS,
	REDIRECT_PARTNERS,
	ROOM_GUESTS_SEPARATOR,
	apartmentsTrans,
	DEFAULT_SEARCH_ENTITIES,
} from '@/app/constants'
import {
	parseIsoDateOnlyToLocalDate,
	parseStayDateFromUrlOrLegacy,
	requireStayDateFromIso,
} from '@/app/utils/calendar-date'
import { getPartnerDisplayData, PartnerDisplayData } from '@/app/utils/partner-mapping'
import countrytoCurrency from 'country-to-currency'
import { Currencies } from 'currencies-map'
import {
	addDays,
	addMonths,
	addWeeks,
	differenceInDays,
	differenceInMilliseconds,
	format,
	formatISO,
	isValid,
	parseISO,
} from 'date-fns'
import { enGB } from 'date-fns/locale'
import { startCase } from 'lodash'
import queryString from 'query-string'
import { remove } from 'remove-accents'
import {
	Allocation,
	DestPagesProps,
	FormType,
	HotelItem,
	InternationalizedStringValue,
	LocationType,
	PageDataObject,
	SearchItemLocationType,
	SearchItemType,
	SearchPlaceType,
} from './types'
import { SearchItem } from './types/index'
import { getLanguageByLang, getLocaleCodeByLang } from './languages'
import { HotelsQueryIncludedEntityTypesEnum } from './types/skyscanner-wrapper-api'

export function formatNumber(value: number) {
	const nFormat = new Intl.NumberFormat('en-US')
	return nFormat.format(value)
}

export function isAirport(value: string) {
	return value.toLowerCase() === SearchItemLocationType.AIRPORT
}

export function isFreeReg(value: string) {
	return value.toLowerCase() === SearchItemLocationType.FREEREGION
}

export const isFromUS = (countryCode: string) => {
	return countryCode.toUpperCase() === 'US'
}

export function isLandmark(value: string) {
	return value ? value.toLowerCase() === SearchItemLocationType.LM : false
}
export function isHood(value: string) {
	return value ? value.toLowerCase() === SearchItemLocationType.HOOD : false
}

export const getDateLocaleByLangCode = (langCode: string) => getLanguageByLang(langCode).dateLocale

export const getRentalContractDuration = (startDate: Date, endDate: Date) => {
	const diffDays = differenceInDays(endDate, startDate)
	const dta = differenceInMilliseconds(startDate, new Date())
	const dtaDiff = Math.ceil(dta / (1000 * 60 * 60 * 24))
	return { diffDays, dtaDiff }
}

export const getAdultsNum = (rooms: Allocation[]) => {
	return rooms
		.map((item) => {
			return item.adults
		})
		.join(ROOM_GUESTS_SEPARATOR)
}

export const getChildrenNum = (rooms: Allocation[]) => {
	return rooms
		.map((item) => {
			return item.childAge.length
		})
		.join(ROOM_GUESTS_SEPARATOR)
}

type RedirectTabData = SearchItem & {
	dateFrom: Date | null
	dateTo: Date | null
	roomsAndGuests: Allocation[]
	formType?: FormType
	backTabPartner?: string | null
	frontTabPartner?: string | null
	buid?: string | null
	fuid?: string | null
	pt?: string | null
}

export const getFormData = (data: RedirectTabData) => {
	const checkInDate = data.dateFrom as Date
	const checkOutDate = data.dateTo || new Date()
	const buid = data.buid
	const pt = data.pt
	const ptid = data.ptid || ''
	// const fbp = getFbClientId();
	const partnerParams = {
		...(data.backTabPartner && { b: data.backTabPartner }),
		...(data.formType === FORM_TYPE.FRONT
			? {
				f: data.frontTabPartner || REDIRECT_PARTNERS.BOOKING,
				...(data.fuid ? { fuid: data.fuid } : {}),
			}
			: {}),
	}

	let destinationId = ''
	let destinationKey
	let city = ''
	const { diffDays, dtaDiff } = getRentalContractDuration(
		checkInDate,
		checkOutDate
	)

	const cityNameRaw = decodeURIComponent(data.cityonly)
	const cityName = startCase(cityNameRaw)

	let destination = data.displayname
	let locationName = data.displayname
	const hotelLocation = `${data.name}, ${cityName}, ${startCase(
		data.country || ''
	)}`

	switch (data.kayakType) {
		case 'ap':
			destinationId = data.ap
			destinationKey = 'airport'
			city = cityName
			break
		case 'city':
			destinationId = ptid && 'C:' + ptid
			destinationKey = 'city'
			city = cityName
			break
		case 'hotel':
			const hasPtid = !!ptid
			destinationId = hasPtid ? `H:${ptid!.substring(1)}` : data.kayakId
			destinationKey = 'hotel'
			destination = hotelLocation
			locationName = hotelLocation
			city = cityName
			break
		default:
			break
	}

	if (data.googlePlaceId) {
		destination = cityName
		locationName = `${cityName}, ${data.country || ''}`
	}

	const uppercaseCountry = startCase(remove(data.country || ''))

	// Check if this is a Booking.com redirect (f=b or b=b) to add hf=3 parameter
	const isBookingRedirect = data.frontTabPartner === 'b' || data.backTabPartner === 'b'

	const formData = {
		tab: data.formType?.toLowerCase() || FORM_TYPE.BACK.toLowerCase(),
		vert: 'hotels',
		'location-name': locationName,
		'hotel-country': uppercaseCountry,
		'state-code': data.rc ? data.rc : data.region,
		...(data.cc && { 'country-code': data.cc }),
		'check-in-date': formatISO(checkInDate),
		'check-out-date': formatISO(checkOutDate),
		// ...(destinationId && { 'destination-id': destinationId }),
		'destination-key': destinationKey,
		destination,
		city: cityName,
		rooms: data.roomsAndGuests.length,
		_adults: getAdultsNum(data.roomsAndGuests),
		_children: getChildrenNum(data.roomsAndGuests),
		los: diffDays,
		dta: dtaDiff,
		...partnerParams,
		...(buid && { buid }),
		...(pt && { pt }),
		...(isBookingRedirect && { hf: '3' }), // Add hf=3 for Booking.com apartment filtering
		// ...(fbp && { fbp }),
	}

	return formData
}

export const addHiddenInputToForm = (
	form: HTMLFormElement,
	name: string,
	value: string
) => {
	const input = document.createElement('input')
	input.type = 'hidden'
	input.name = name
	input.setAttribute('value', value)
	form.appendChild(input)
}

export const submitHiddenForm = (
	data: Record<string, any>,
	form: HTMLFormElement
) => {
	form.innerHTML = '' // Clear previous inputs
	for (const key in data) {
		if (key.substring(0, 1) === '_') {
			const newKey = key.substring(1, key.length)
			const dataArray = data[key].split('|')
			for (let i = 0; i < dataArray.length; i++) {
				addHiddenInputToForm(form, newKey, dataArray[i])
			}
		} else {
			addHiddenInputToForm(form, key, data[key])
		}
	}
	form.submit()
}

export const smoothScrollTo = (to: number, offset = 10) => {
	if (typeof window === 'undefined') return
	window.scrollTo({
		top: to + offset,
		behavior: 'smooth',
	})
}

const getValueByLanguage = (
	array: InternationalizedStringValue[] | undefined,
	languageKey: string
) => {
	const localizedValue = array?.find((entry) => entry._key === languageKey)
	return localizedValue ? localizedValue.value : ''
}

export const getLocalizedValues = (data: PageDataObject, lang: string) => {
	const key = getLocaleCodeByLang(lang)
	return {
		...data,
		headingOne: getValueByLanguage(data.headingOne, key),
		headingTwo: getValueByLanguage(data.headingTwo, key),
		metaTitle: getValueByLanguage(data.metaTitle, key),
		metaDescription: getValueByLanguage(data.metaDescription, key),
	}
}

// 8.3 -> 8.3
// 8 -> 8.0
// 10 -> 10
export function getRatingValue(num: number) {
	if (Number.isInteger(num) && num < 10) {
		return num.toFixed(1)
	}

	return num
}
// This is for title translation on the Hotels page
const getTargetLocation = async (params: DestPagesProps) => {
	const lang = params?.params.lang
	let [country, region, city, hotel] = params?.params.dest || []

	let loctype = params?.searchParams.loctype || null
	let searchQuery = ''

	if (hotel) {
		searchQuery = decodeURIComponent(hotel)
	} else if (city) {
		searchQuery = `${city}, ${region} ${country}`
	} else {
		searchQuery = `${region} ${country}`
	}

	if (country) {
		loctype = SearchItemLocationType.COUNTRY
	}

	if (region) {
		loctype = SearchItemLocationType.REGION
	}

	if (city) {
		loctype = SearchItemLocationType.CITY
	}

	if (city === SearchItemLocationType.AIRPORT) {
		loctype = SearchItemLocationType.AIRPORT
	}

	if (city === SearchItemLocationType.FREEREGION) {
		loctype = SearchItemLocationType.FREEREGION
	}

	if (hotel && city !== SearchItemLocationType.AIRPORT) {
		loctype = SearchItemLocationType.HOTEL
	}

	if (params?.searchParams.loctype) {
		loctype = params?.searchParams.loctype
	}

	const results = await fetchAutocompleteResults(
		searchQuery
			.split(' ')
			.filter((item) => item !== 'undefined' && item !== 'ap' && item !== 'lm')
			.join(' '),
		lang
	)
		.then((res) => {
			return loctype
				? res.find((item) => item.loctype === loctype) || res[0]
				: res[0]
		})
		.catch((e) => {
			console.log('Error getTargetLocation:', e)
			return null
		})

	return results
}

export const getDisplayData = async (params: DestPagesProps) => {
	const location = await getTargetLocation(params)

	const {
		country: displayCountry,
		cityonly,
		region: displayRegion,
		name: locationName,
		kayakType,
		displayname,
	} = location || {}

	const rawData = {
		cityonly,
		region: displayRegion,
		name: locationName,
		kayakType,
		displayname,
	}

	const filteredData = Object.fromEntries(
		Object.entries(rawData).filter(([key, value]) => value !== undefined)
	)

	const data = {
		country: displayCountry,
		city: filteredData.cityonly,
		displayName: displayname,
		...(filteredData.region === filteredData.cityonly
			? {}
			: { region: filteredData.region }),
		...(kayakType === SearchItemLocationType.HOTEL
			? { hotel: filteredData.name }
			: {}),
	}

	return data
}

export const getRatingColor = (rating: number): string => {
	switch (true) {
		case rating < RATING_THRESHOLD.LOW:
			return RATING_COLOR.LOW
		case rating < RATING_THRESHOLD.MEDUIM:
			return RATING_COLOR.MEDIUM
		default:
			return RATING_COLOR.HIGH
	}
}

//query : C:1231, R:4561, NYC
export const getLocationTypeByQuery = (query: string): LocationType | null => {
	if (!query) return null

	if (query.length === 3) {
		return LocationType.AIRPORT
	}

	const prefix = query.charAt(0).toLowerCase()
	const prefixToEndpointMap: { [key: string]: LocationType } = {
		c: LocationType.CITY,
		h: LocationType.HOTEL,
		r: LocationType.CITY,
	}

	const endpoint = prefixToEndpointMap[prefix]

	if (!endpoint) {
		return null
	}

	return endpoint
}

export const getEntityTypesByQuery = (query: string): HotelsQueryIncludedEntityTypesEnum[] | undefined => {
	if (!query) return

	if (query.length === 3) {
		return [HotelsQueryIncludedEntityTypesEnum.PLACE_TYPE_AIRPORT]
	}

	const prefix = query.charAt(0).toLowerCase()
	const prefixToEntityTypesMap: { [key: string]: HotelsQueryIncludedEntityTypesEnum[] } = {
		c: [HotelsQueryIncludedEntityTypesEnum.PLACE_TYPE_CITY],
		h: [HotelsQueryIncludedEntityTypesEnum.PLACE_TYPE_HOTEL],
		r: [
			HotelsQueryIncludedEntityTypesEnum.PLACE_TYPE_COUNTRY,
			HotelsQueryIncludedEntityTypesEnum.PLACE_TYPE_DISTRICT,
			HotelsQueryIncludedEntityTypesEnum.PLACE_TYPE_ISLAND,
			HotelsQueryIncludedEntityTypesEnum.PLACE_TYPE_FIRST_LEVEL_NATION_ADMINISTRATIVE_DIVISION,
			HotelsQueryIncludedEntityTypesEnum.PLACE_TYPE_SECOND_LEVEL_NATION_ADMINISTRATIVE_DIVISION,
			HotelsQueryIncludedEntityTypesEnum.PLACE_TYPE_CITY,
			HotelsQueryIncludedEntityTypesEnum.PLACE_TYPE_SEA_COAST,
			HotelsQueryIncludedEntityTypesEnum.PLACE_TYPE_MOUNTAIN_RANGE,
			HotelsQueryIncludedEntityTypesEnum.PLACE_TYPE_SEA,
		],
	}

	const entityTypes = prefixToEntityTypesMap[prefix]

	if (!entityTypes) {
		return DEFAULT_SEARCH_ENTITIES
	}

	return entityTypes
}

export const parseSearchParamsToLower = (
	searchParams?: Record<string, any>
) => {
	if (!searchParams) return null

	const params = new URLSearchParams(searchParams)
	const lowerCaseParams: { [key: string]: string } = {}
	params.forEach((value, key) => {
		if (
			typeof value === 'string' &&
			typeof key === 'string' &&
			value !== 'undefined'
		) {
			lowerCaseParams[key.toLowerCase()] = value
		}
	})

	return lowerCaseParams
}

export const getCurrencyByCountry = (countryCode: string): string => {
	if (!countryCode) return DEFAULT_CURRENCY

	const currency =
		countrytoCurrency[
		countryCode.toUpperCase() as keyof typeof countrytoCurrency
		]
	return currency || DEFAULT_CURRENCY
}

/**
 * Validates whether a given date string represents a date that is in the future.
 *
* @param {string} [date] - ISO / date-only string; `yyyy-MM-dd` is handled consistently with stay-date helpers (local calendar day via date-fns `parseISO`).
 * @returns {string | null} - Returns the original date string if it is in the future, otherwise returns null.
 */
export function checkDate(date?: string | null): string | null {
	if (!date) return null

	const parsedDate = parseISO(date)
	return differenceInDays(parsedDate, new Date()) >= 0 ? date : null
}

/**
 * Validates if both check-in and check-out dates are present and valid (future dates)
 * @param checkIn - Check-in date string
 * @param checkOut - Check-out date string
 * @returns boolean indicating if both dates are valid
 */
export function hasValidDates(
	checkIn?: string | null,
	checkOut?: string | null
): boolean {
	return !!(checkIn && checkOut && checkDate(checkIn) && checkDate(checkOut))
}

/**
 * Checks if date parameters are missing or invalid for hotel searches
 * @param checkIn - Check-in date string
 * @param checkOut - Check-out date string
 * @returns boolean indicating if dates need fallback to defaults
 */
export function needsHotelDateFallback(
	checkIn?: string | null,
	checkOut?: string | null
): boolean {
	return !checkIn || !checkOut || !checkDate(checkIn) || !checkDate(checkOut)
}

/**
 * Validates if date parameters need fallback for compact booking display
 * @param checkInParam - Check-in date parameter
 * @param checkOutParam - Check-out date parameter
 * @returns boolean indicating if default dates should be used
 */
export function shouldUseCompactBookingDefaults(
	checkInParam?: string | null,
	checkOutParam?: string | null
): boolean {
	return (
		!checkInParam ||
		!checkOutParam ||
		!checkDate(checkInParam) ||
		!checkDate(checkOutParam)
	)
}

/**
 * Determines if default dates should be applied based on page type and date validity
 * @param checkInParam - Check-in date parameter from URL
 * @param checkOutParam - Check-out date parameter from URL
 * @param isNotMainPage - Whether this is the main page
 * @returns boolean indicating if default dates should be used
 */
export function shouldUseFallbackDates(
	checkInParam?: string | null,
	checkOutParam?: string | null,
	pageAllowsFallback?: boolean
): boolean {
	return !!(
		pageAllowsFallback &&
		(!checkInParam ||
			!checkOutParam ||
			(checkInParam && !checkDate(checkInParam)) ||
			(checkOutParam && !checkDate(checkOutParam)))
	)
}

/**
 * Gets the appropriate default dates based on location type
 * @param isHotel - Whether the selected location is a hotel
 * @returns object with checkIn and checkOut Date objects
 */
export function getDefaultDatesForLocationType(isHotel: boolean): {
	checkIn: Date
	checkOut: Date
} {
	if (isHotel) {
		const checkIn = addWeeks(new Date(), 1) // HOTEL_DEFAULT_CHECK_IN_WEEKS
		const checkOut = addDays(checkIn, 2) // HOTEL_DEFAULT_STAY_DAYS
		return { checkIn, checkOut }
	} else {
		return {
			checkIn: new Date(DEFAULT_CHECK_IN_DATE), // DEFAULT_CHECK_IN_DATE
			checkOut: new Date(DEFAULT_CHECK_OUT_DATE), // DEFAULT_CHECK_OUT_DATE
		}
	}
}

/**
 * Validates that dates are set and check-out date is not more than 12 months from today
 * @param dateFrom - Check-in date
 * @param dateTo - Check-out date
 * @returns boolean indicating if dates are valid and in range
 */
export function validateDateRange(
	dateFrom: Date | null,
	dateTo: Date | null
): boolean {
	if (!dateFrom || !dateTo) {
		return false
	}

	const maxAllowedDate = addMonths(new Date(), 12)
	if (dateTo > maxAllowedDate) {
		return false
	}

	return true
}

export const randomizeUserAgents = (userAgents: string[]): string => {
	const randomIndex = Math.floor(Math.random() * userAgents.length)
	return userAgents[randomIndex]
}

export const isBotUA = (userAgent: string) => {
	const lowerCaseAgent = userAgent.toLowerCase()
	const bots = [
		'bot',
		'spider',
		'crawler',
		'googlebot',
		'bingbot',
		'yandexbot',
		'bytespider',
		'google-read-aloud',
	]
	return bots.some((indicator) => lowerCaseAgent.includes(indicator))
}

export const removeMaxHeightFromUrl = (url: string) => {
	const fallbackImagePath = '/images/no-image.png'

	if (!url) {
		return fallbackImagePath
	}
	const urlObject = new URL(url)
	urlObject.searchParams.delete('maxheight')
	return urlObject.toString()
}

export const isImageVertical = (src: string) => {
	const image = new Image()
	image.src = src
	return image.naturalHeight > image.naturalWidth
}

export const validateEmail = (email: string) => {
	const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
	return re.test(String(email).toLowerCase())
}

export const isRtlLanguage = (lang: string) => getLanguageByLang(lang).isRtl

export const parseQueryString = (str: string) => {
	return queryString.parse(str)
}
export const serialize = (obj: Record<string, any>): string => {
	const str = []
	for (const p in obj) {
		if (obj.hasOwnProperty(p)) {
			str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]))
		}
	}
	return str.join('&')
}

export async function getPlaceDetails(placeId: string) {
	const { Place } = (await google.maps.importLibrary(
		'places'
	)) as google.maps.PlacesLibrary

	const place = new Place({
		id: placeId,
		requestedLanguage: 'en', // optional
	})

	// Call fetchFields, passing the desired data fields.
	await place.fetchFields({
		fields: [
			'displayName',
			'formattedAddress',
			'location',
			'id',
			'addressComponents',
			'utcOffsetMinutes',
			'photos',
			'primaryType',
			'primaryTypeDisplayName',
		],
	})

	const {
		displayName,
		formattedAddress,
		location,
		id,
		addressComponents,
		utcOffsetMinutes,
		photos,
		primaryType,
		primaryTypeDisplayName,
	} = place

	return {
		displayName,
		formattedAddress,
		location: {
			lat: location?.lat(),
			lng: location?.lng(),
		},
		id,
		utc: utcOffsetMinutes,
		addressComponents,
		displayImages: photos?.map((photo) => photo.getURI({ maxWidth: 200 })),
		displayType: {
			type: primaryType,
			displayName: primaryTypeDisplayName,
		},
	}
}
export const capitalize = (s: string) => {
	if (typeof s !== 'string') return ''
	return s.charAt(0).toUpperCase() + s.slice(1)
}

export const apartmentsTranslate = (lang: string, isApartments: boolean, defaultTrans: string, isLandmark?: boolean, isLoading?: boolean) => {
	let translatedString = apartmentsTrans[lang as keyof typeof apartmentsTrans];
	let resultString = defaultTrans;
	if (translatedString && isApartments) {
		if (isLandmark) {
			resultString = translatedString.landmark;
		} else if (isLoading) {
			resultString = translatedString.loading;
		} else {
			resultString = translatedString.city;
		}
	}
	return resultString;
}

export const getGuestsCount = (rooms: Allocation[]) => {
	return rooms.reduce((total, room) => {
		return total + room.adults + room.childAge.length
	}, 0)
}

export const getValidDateFromParams = (
	searchParams: URLSearchParams,
	paramName: string,
	defaultDate: Date
): Date => {
	const dateParam = searchParams.get(paramName)
	if (dateParam && checkDate(dateParam)) {
		return parseISO(dateParam)
	}
	return defaultDate
}

export const getAppliedFiltersFromURL = (
	searchParams: URLSearchParams
): {
	stars: number[]
	rating: number[]
	facilities: number[]
	stayOptions: number[]
	hotelStyles: number[]
	priceRange: [number, number]
	priceRangeLimits?: [number, number]
} => {
	const appliedFilters: {
		stars: number[]
		rating: number[]
		facilities: number[]
		stayOptions: number[]
		hotelStyles: number[]
		priceRange: [number, number]
		priceRangeLimits?: [number, number]
	} = {
		stars: [],
		rating: [],
		facilities: [],
		stayOptions: [],
		hotelStyles: [],
		priceRange: [0, 0],
	}

	// Stars
	const starsParam = searchParams.get('s')
	if (starsParam) {
		appliedFilters.stars = starsParam.split('|').map(Number)
	}

	// Rating
	const ratingParam = searchParams.get('gr')
	if (ratingParam) {
		appliedFilters.rating = [Math.min(...ratingParam.split('|').map(Number))]
	}

	// Facilities
	const facilitiesParam = searchParams.get('fa')
	if (facilitiesParam) {
		appliedFilters.facilities = facilitiesParam.split('|').map(Number)
	}

	// Stay Options
	const stayOptionsParam = searchParams.get('p')
	if (stayOptionsParam) {
		const validStayOptionIds = new Set(STAY_OPTIONS.map((option) => option.id))
		appliedFilters.stayOptions = stayOptionsParam
			.split('|')
			.map(Number)
			.filter((id) => validStayOptionIds.has(id))
	}

	// Hotel Styles
	const hotelStylesParam = searchParams.get('t')
	if (hotelStylesParam) {
		appliedFilters.hotelStyles = hotelStylesParam.split('|').map(Number)
	}

	// Price Range
	const minPriceParam = searchParams.get('mn')
	const maxPriceParam = searchParams.get('mx')

	if (minPriceParam || maxPriceParam) {
		appliedFilters.priceRange = [
			minPriceParam ? parseInt(minPriceParam, 10) : 0,
			maxPriceParam ? parseInt(maxPriceParam, 10) : 0,
		]
	}

	// Price Limits
	const minPriceLimitParam = searchParams.get('lmn')
	const maxPriceLimitParam = searchParams.get('lmx')

	if (minPriceLimitParam && maxPriceLimitParam) {
		appliedFilters.priceRangeLimits = [
			minPriceLimitParam ? parseInt(minPriceLimitParam, 10) : 0,
			maxPriceLimitParam ? parseInt(maxPriceLimitParam, 10) : 0,
		]
	}

	return appliedFilters
}

export const isApartment = (value: number) => {
	return value === PROPERTY_TYPE.APARTMENT
}
export const isHotel = (value: number) => {
	return value === PROPERTY_TYPE.HOTEL
}

export const isCountry = (path: string | string[]) => {
	if (Array.isArray(path)) {
		return path.length === 1
	}
	return path.split('/').length === 1
}

export const scrollToSelectedMonth = (dateFrom: Date) => {
	const today = new Date()
	const monthContainers = document.querySelectorAll(
		'.react-datepicker__month-container'
	)

	const selectedDate = dateFrom || new Date()
	const monthsDiff =
		(selectedDate.getFullYear() - today.getFullYear()) * 12 +
		selectedDate.getMonth() -
		today.getMonth()

	if (monthContainers[monthsDiff] && monthsDiff >= 0) {
		const headerElement = monthContainers[monthsDiff].querySelector(
			'.react-datepicker__header'
		) as HTMLElement
		const container = document.querySelector(
			'.mobile-datepicker .react-datepicker'
		) as HTMLElement

		if (headerElement) {
			const offset = 35
			const scrollPosition = headerElement.offsetTop - offset
			headerElement.style.paddingTop = '20px'
			container.scrollTo({
				top: scrollPosition,
				behavior: 'instant',
			})
		} else {
			console.log('Header element not found in the selected month container')
		}
	} else {
		console.log('Selected month is not visible in the current view')
	}
}

export const calculateDisplayedMaxPrice = (
	minPrice: number,
	maxPrice: number
): number => {
	if (minPrice < 10) {
		return Math.min(Math.ceil(minPrice * 10), maxPrice)
	}

	const roundedPrice = Math.floor(maxPrice / 100) * 100	// TODO: This fails for prices less then 100
	if(roundedPrice === 0 ) return maxPrice

	if (roundedPrice > maxPrice) {
		let result
		if (maxPrice < 100) {
			result = Math.floor(maxPrice / 10) * 10
		} else {
			result = Math.floor(maxPrice / 100) * 100
		}

		// If the result is less than or equal to minPrice, just return maxPrice
		if (result <= minPrice) {
			return maxPrice
		}

		if (result === 0) return maxPrice
		return result
	}

	return roundedPrice
}

export const determineSearchPlaceFromPath = (
	pathComponents: string[],
	hasHotelInPath: boolean
) => {
	const country = pathComponents[0] || ''
	const region = pathComponents[1] || ''
	const city = pathComponents[2] || ''
	const hotel = pathComponents[3] || ''
	const place = pathComponents[4] || ''

	let type: SearchPlaceType = SearchPlaceType.COUNTRY
	let name = country

	if (region) {
		type = SearchPlaceType.REGION
		name = `${region}, ${name}`
	}

	if (city) {
		type = SearchPlaceType.CITY
		name = `${city}, ${name}` 
	}

	if (hotel) {
		type = SearchPlaceType.HOTEL
		name = hasHotelInPath ? hotel : `${hotel}, ${city}, ${country}`

		if (isLandmark(hotel) || isHood(hotel)) {
			type = SearchPlaceType.CITY // landmark search to city
			name = `${place}`
		}
	}

	if (isAirport(city)) {
		type = SearchPlaceType.AIRPORT
		name = `${hotel}, ${country}`
	}

	if (isFreeReg(city)) {
		type = SearchPlaceType.REGION
		name = `${country}`
	}

	return { type, name }
}

export const getCurrencySymbol = (currency: string) => {
	return Currencies.symbols.get(currency.toUpperCase()) || currency
}

export const getHotelImagesArray = (hotelData: HotelItem) => {
	const rawData = hotelData.rawHotelData

	// big image
	const mainImage = rawData.mainImage?.large
		? rawData.mainImage
		: rawData.image && rawData.largeImage
			? { small: rawData.image, large: rawData.largeImage }
			: null

	// Combine main image with additional images
	return [...(mainImage ? [mainImage] : []), ...(rawData.moreImages ?? [])]
}

export const getRedirectPartnerInfo = (searchParams: URLSearchParams) => {
	const backTabPartner = searchParams.get('b') || null
	const frontTabPartner = searchParams.get('f') || null

	return {
		backTabPartner,
		frontTabPartner,
		hasBackTabRedirect: !!(backTabPartner && backTabPartner !== '0'),
		hasFrontPartner: !!(frontTabPartner && frontTabPartner !== '0'),
		isPartnerBooking: backTabPartner === 'b' || backTabPartner === 'bb',
		partnerInfo: backTabPartner ? getPartnerDisplayData(backTabPartner) : null,
	}
}

export const getComparisonCheckboxText = (
	partnerInfo: PartnerDisplayData | null,
	isPartnerBooking: boolean,
	t: (key: string) => string
) => {
	if (isPartnerBooking && partnerInfo) {
		// For booking partners (b=b or b=bb)
		return {
			leftLabel: t('compareWith'),
			rightLabel: partnerInfo.displayName
		}
	} else if (partnerInfo) {
		// For other partners (b=ex, b=k, etc.)
		return {
			leftLabel: '',
			rightLabel: t('compareWithOthers')
		}
	}

	// Default fallback (should not happen with proper conditional rendering)
	return {
		leftLabel: t('compareWith'),
		rightLabel: t('booking.com') || 'Booking.com'
	}
}

export const truncateTextAtWordBoundary = (
	text: string,
	targetWords: number = 90
): { truncated: string; isComplete: boolean } => {
	const words = text.split(' ')

	if (words.length <= targetWords) {
		return { truncated: text, isComplete: true }
	}

	// Look for sentence endings between 80-90 words
	const searchStart = Math.max(0, targetWords - 10)
	const searchEnd = Math.min(words.length, targetWords)

	for (let i = searchEnd - 1; i >= searchStart; i--) {
		const word = words[i]
		if (word.endsWith('.') || word.endsWith('!') || word.endsWith('?')) {
			const truncatedText = words.slice(0, i + 1).join(' ')
			// Remove the last punctuation mark and add ellipsis
			const withoutPunctuation = truncatedText.replace(/[.!?]$/, '')
			return { truncated: withoutPunctuation + '...', isComplete: false }
		}
	}

	// If no sentence ending found, truncate at target word count
	const truncatedText = words.slice(0, targetWords).join(' ')
	return { truncated: truncatedText + '...', isComplete: false }
}
// Returns a rectangular polygon string for the current map bounds
// Format: polygon:lat,lng;lat,lng;lat,lng;lat,lng (NW;NE;SE;SW)
export const getBoundsPolygonString = (
	mapLike: any,
	precision: number = 3
): string | null => {
	try {
		if (!mapLike || typeof mapLike.getBounds !== 'function') return null
		const bounds = mapLike.getBounds()
		if (!bounds) return null
		const ne = bounds.getNorthEast()
		const sw = bounds.getSouthWest()
		const neLat = ne.lat()
		const neLng = ne.lng()
		const swLat = sw.lat()
		const swLng = sw.lng()
		const nwLat = neLat
		const nwLng = swLng
		const seLat = swLat
		const seLng = neLng
		const fmt = (n: number) => n.toFixed(precision)
		return `polygon:${fmt(nwLat)},${fmt(nwLng)};${fmt(neLat)},${fmt(neLng)};${fmt(seLat)},${fmt(seLng)};${fmt(swLat)},${fmt(swLng)}`
	} catch {
		return null
	}
}

export const isLowercaseMonth = (lang: string) => ['cz', 'fi', 'ro'].includes(lang)

export const parseSlugToTitle = (slug: string): string => {
	try {
		const decodedSlug = decodeURIComponent(slug)
		const title = decodedSlug
			.replace(/-/g, ' ') // Replace dashes with spaces
			.replace(/\s+/g, ' ') // Replace multiple spaces with single space
			.trim() // Remove leading/trailing spaces

		return title
	} catch (error) {
		// If decoding fails, fallback to basic dash replacement
		console.warn(`Failed to decode slug: ${slug}`, error)
		return slug.replace(/-/g, ' ').replace(/\s+/g, ' ').trim()
	}
}

/**
 * Strips HTML tags from a string and returns plain text.
 * Useful for extracting plain text from translations that contain HTML markup
 * (e.g., for alt attributes, meta descriptions, etc.).
 *
 * @param htmlString - The string containing HTML tags
 * @returns The plain text with all HTML tags removed
 *
 * @example
 * ```
 * stripHtmlTags('<light>PICK</light> YOUR <priceBreak>PRICE</priceBreak>')
 * // Returns: 'PICK YOUR PRICE'
 * ```
 */
export const stripHtmlTags = (htmlString: string): string => {
	return htmlString.replace(/<[^>]*>/g, '').trim()
}

export const parseGooglePlaceToSelectedItem = async (
	placeId: string
): Promise<SearchItem> => {
	const place = await getPlaceDetails(placeId)

	const city =
		place.addressComponents?.find((component) =>
			component.types.includes('locality')
		)?.longText ||
		place.addressComponents?.find((component) =>
			component.types.includes('administrative_area_level_2')
		)?.longText
	const country = place.addressComponents?.find((component) =>
		component.types.includes('country')
	)?.longText
	const region =
		place.addressComponents?.find((component) =>
			component.types.includes('administrative_area_level_1')
		)?.longText || city

	return {
		id: place.id,
		lat: place.location.lat as number,
		lng: place.location.lng as number,
		name: place.displayName as string,
		displayname: place.displayName as string,
		country: country || '',
		region: region || '',
		cityonly: city || '',
		loctype: SearchItemLocationType.LM,
		placeID: place.id,
		smartyDisplay: place.formattedAddress as string,
		locationname: place.formattedAddress as string,
		cc:
			place.addressComponents?.find((component) =>
				component.types.includes('country')
			)?.shortText || '',
		rc: region || '',
		cityname: `${city || ''}, ${country || ''}`,
		timezone: null, // setting a default one
		utc: format(new Date().setUTCHours(place.utc as number, 0), 'xxx'),
		airportname: '',
		ap: '',
		apicode: '',
		ctid: '',
		displayType: place.displayType as SearchItemType,
		indexId: '',
		isMetroOnly: false,
		kayakId: '',
		kayakType: 'city',
		ptid: '',
		searchFormPrimary: place.displayName as string,
		searchFormSecondary: place.displayName as string,
		secondary: city || '',
		shortdisplayname: '',
		subregion: '',
		entityKey: '',
		googlePlaceId: place.id,
		utcOffsetMinutes: place.utc as number,
	} as SearchItem
}

export const getPriceSymbol = (currency: string) => {
	return Currencies.symbols.get(currency) || ''
}

export const parseRoomsFromUrl = (
	searchParams: URLSearchParams
): Allocation[] => {
	if (searchParams.has('room')) {
		let roomParam: string[] = searchParams.getAll('room')

		if (!Array.isArray(roomParam)) {
			roomParam = [roomParam]
		}

		const rooms = roomParam.map((room: string) => {
			const [adults, children = ''] = room.split(ROOM_GUESTS_SEPARATOR)
			const childAges = children
				? children.split(',').map((age) => Number(age))
				: []

			return {
				adults: parseInt(adults) || 2,
				childAge: childAges,
			}
		})

		return rooms
	}

	// Fallback to default allocation
	return [DEFAULT_ALLOCATION]
}

export const parseDatesFromUrl = (
	searchParams: URLSearchParams,
	isHotel = false
): { checkInDate: Date; checkOutDate: Date } => {
	const defaultCheckInDate = isHotel
		? DEFAULT_HOTEL_CHECK_IN_DATE
		: DEFAULT_CHECK_IN_DATE
	const defaultCheckOutDate = isHotel
		? DEFAULT_HOTEL_CHECK_OUT_DATE
		: DEFAULT_CHECK_OUT_DATE

	const resolveStayDate = (
		hasParam: boolean,
		paramValue: string | null,
		fallbackIso: string
	): Date => {
		if (hasParam && paramValue) {
			const fromParam = parseIsoDateOnlyToLocalDate(paramValue)
			if (fromParam) return fromParam
		}
		return requireStayDateFromIso(
			fallbackIso,
			'parseDatesFromUrl: default stay date must be yyyy-MM-dd'
		)
	}

	const checkInDate = resolveStayDate(
		searchParams.has('check-in-date'),
		searchParams.get('check-in-date'),
		defaultCheckInDate
	)

	const checkOutDate = resolveStayDate(
		searchParams.has('check-out-date'),
		searchParams.get('check-out-date'),
		defaultCheckOutDate
	)

	return {
		checkInDate,
		checkOutDate,
	}
}

/**
 * Formats a date string with proper weekday abbreviations for different languages.
 * Stay dates (`yyyy-MM-dd`) are parsed as calendar days; other shapes go through `parseISO` / `Date`.
 * @param dateString - Typically `yyyy-MM-dd` (DestinationCard) or other parseable input
 * @param lang - The language code
 * @returns Formatted date string with weekday, day and month
 */
export const getFormattedDate = (dateString: string, lang: string, formatString?: string): string => {
	const date = parseStayDateFromUrlOrLegacy(dateString)
	if (!isValid(date)) {
		return dateString
	}
	const locale = getDateLocaleByLangCode(lang)

	if (lang === 'ru') {
		// Russian 2-letter weekday abbreviations
		const russianWeekdays = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']
		const weekday = russianWeekdays[date.getDay()]
		const dayMonth = format(date, 'd MMM', { locale })
		return `${weekday}, ${dayMonth}`
	}

	if (lang === 'ja') {
		// Japanese native date style: 12月10日（水）
		// Use narrow weekday (EEE) and Japanese full-width parentheses
		return format(date, formatString || 'M月d日（EEE）', { locale })
	}

	// For other languages, use the standard format
	return format(date, formatString || 'EEE, d MMM', { locale })
}

export const ratingsTranslations = (rating: number) => {
	switch (true) {
		case rating < 4:
			return 'poor'
		case rating >= 4 && rating < 5:
			return 'mediocre'
		case rating >= 5 && rating < 7:
			return 'okay'
		case rating >= 7 && rating < 8:
			return 'good'
		case rating >= 8 && rating < 8.5:
			return 'veryGood'
		case rating >= 8.5 && rating < 9:
			return 'fabulous'
		case rating >= 9 && rating < 9.5:
			return 'superb'
		default:
			return 'exceptional'
	}
}

// Decoder from Google docs
// https://developers.google.com/identity/gsi/web/guides/display-google-one-tap#javascript
export const decodeJwtResponse = (token: string) => {
	let base64Url = token.split('.')[1];
	let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	let jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
		return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
	}).join(''));

	return JSON.parse(jsonPayload);
}

export const replaceLocaleInPathname = (
	pathname: string,
	currentLocale: string,
	newLocale: string
): string => {
	const pathSegments = pathname.split('/').filter(Boolean)

	// Replace the first segment (current locale) with the new locale
	if (pathSegments[0] === currentLocale) {
		pathSegments[0] = newLocale
	}

	return '/' + pathSegments.join('/')
}